import { useMemo, useState } from "react";
import { DetailHeader } from "@/components/layout/detail-header";
import { Button } from "@/components/ui/button";
import { useToast } from "@/contexts/toast-context";
import { Combobox } from "@/components/ui/combobox";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

type ServerHost = "adp.flatfile.com" | "qa.adp.flatfile.com";

const SERVER_OPTIONS = [
  { value: "adp.flatfile.com", label: "adp.flatfile.com", description: "Production" },
  { value: "qa.adp.flatfile.com", label: "qa.adp.flatfile.com", description: "QA" },
];

interface CommitItem {
  id: string;
  sheetId?: string;
  createdBy?: string;
  createdAt?: string;
}

interface CommitResult {
  commitId: string;
  success: boolean;
  error?: string;
}

interface CopyProgress {
  phase: "idle" | "streaming" | "uploading" | "complete" | "error";
  message: string;
  recordsRead?: number;
  recordsWritten?: number;
  batchesTotal?: number;
  batchesDone?: number;
}

export default function ToolsPage() {
  const { showError, showSuccess } = useToast();

  // Commit Completion Tool State
  const [commitServer, setCommitServer] = useState<ServerHost>("adp.flatfile.com");
  const [commitToken, setCommitToken] = useState("");
  const [workbookId, setWorkbookId] = useState("");
  const [isLoadingCommits, setIsLoadingCommits] = useState(false);
  const [isCompletingCommits, setIsCompletingCommits] = useState(false);
  const [commits, setCommits] = useState<CommitItem[]>([]);
  const [commitResults, setCommitResults] = useState<CommitResult[]>([]);

  // Sheet Copy Tool State
  const [fromServer, setFromServer] = useState<ServerHost>("adp.flatfile.com");
  const [fromToken, setFromToken] = useState("");
  const [fromSheetId, setFromSheetId] = useState("");
  const [toServer, setToServer] = useState<ServerHost>("adp.flatfile.com");
  const [toToken, setToToken] = useState("");
  const [toSheetId, setToSheetId] = useState("");
  const [truncateDestination, setTruncateDestination] = useState(false);
  const [copyProgress, setCopyProgress] = useState<CopyProgress>({ phase: "idle", message: "" });

  const commitApiBase = useMemo(() => `https://${commitServer}/api/v1`, [commitServer]);

  function authHeader(tokenValue: string) {
    return tokenValue.startsWith("Bearer ") ? tokenValue : `Bearer ${tokenValue}`;
  }

  // ==================== COMMIT COMPLETION FUNCTIONS ====================

  async function fetchCommitJson(url: string) {
    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": authHeader(commitToken),
        "X-Disable-Hooks": "true",
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} ${res.statusText}${text ? `: ${text}` : ""}`);
    }
    return res.json();
  }

  async function postCommitJson(url: string) {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": authHeader(commitToken),
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} ${res.statusText}${text ? `: ${text}` : ""}`);
    }
    return res.json();
  }

  async function loadIncompleteCommits() {
    if (!workbookId || !commitToken) {
      showError("Please enter a workbook ID and token.");
      return;
    }
    setIsLoadingCommits(true);
    setCommits([]);
    setCommitResults([]);

    try {
      const url = `${commitApiBase}/workbooks/${encodeURIComponent(workbookId)}/commits?completed=false`;
      const data = await fetchCommitJson(url);
      const list: CommitItem[] = Array.isArray(data?.data) ? data.data : [];
      setCommits(list);
      if (list.length === 0) {
        showSuccess("No incomplete commits found.");
      } else {
        showSuccess(`Found ${list.length} incomplete commit(s).`);
      }
    } catch (e: any) {
      showError("Couldn't load commits. Please check the server, workbook ID, and token.", "Failed to load commits");
      setCommits([]);
    } finally {
      setIsLoadingCommits(false);
    }
  }

  async function completeAllCommits() {
    if (commits.length === 0) {
      showError("No commits to complete.");
      return;
    }
    setIsCompletingCommits(true);
    setCommitResults([]);

    const newResults: CommitResult[] = [];

    for (const commit of commits) {
      try {
        const url = `${commitApiBase}/commits/${encodeURIComponent(commit.id)}/complete`;
        await postCommitJson(url);
        newResults.push({ commitId: commit.id, success: true });
      } catch (e: any) {
        newResults.push({ commitId: commit.id, success: false, error: e.message });
      }
      setCommitResults([...newResults]);
    }

    const successCount = newResults.filter(r => r.success).length;
    const failCount = newResults.filter(r => !r.success).length;

    if (failCount === 0) {
      showSuccess(`Successfully completed ${successCount} commit(s).`);
    } else {
      showError(`Completed ${successCount} commit(s), ${failCount} failed.`, "Partial completion");
    }

    setIsCompletingCommits(false);
  }

  // ==================== SHEET COPY FUNCTIONS ====================

  async function copySheet() {
    if (!fromSheetId || !fromToken || !toSheetId || !toToken) {
      showError("Please fill in all fields for both source and destination.");
      return;
    }

    setCopyProgress({ phase: "streaming", message: "Starting to stream records from source..." });

    try {
      // Step 1: Stream records from source
      const fromApiBase = `https://${fromServer}/api`;
      const streamUrl = `${fromApiBase}/v2-alpha/records.jsonl?sheetId=${encodeURIComponent(fromSheetId)}&stream=true`;

      const streamResponse = await fetch(streamUrl, {
        method: "GET",
        headers: {
          "Authorization": authHeader(fromToken),
          "Accept": "application/jsonl",
        },
      });

      if (!streamResponse.ok) {
        const errorText = await streamResponse.text().catch(() => "");
        throw new Error(`Failed to stream from source: ${streamResponse.status} ${streamResponse.statusText}${errorText ? ` - ${errorText}` : ""}`);
      }

      const text = await streamResponse.text();
      const records = text
        .trim()
        .split("\n")
        .filter(line => line.length > 0)
        .map(line => {
          try {
            return JSON.parse(line);
          } catch {
            return null;
          }
        })
        .filter(record => record !== null);

      setCopyProgress({
        phase: "streaming",
        message: `Streamed ${records.length} records from source`,
        recordsRead: records.length,
      });

      if (records.length === 0) {
        setCopyProgress({ phase: "complete", message: "No records to copy (source sheet is empty)", recordsRead: 0 });
        showSuccess("Source sheet is empty. Nothing to copy.");
        return;
      }

      // Step 2: Clean records (remove Flatfile metadata)
      const cleanedRecords = records.map(record => {
        const { __k, __s, __n, ...cleaned } = record;
        return cleaned;
      });

      // Step 3: Upload to destination in batches
      const batchSize = 1000;
      const batches: any[][] = [];
      for (let i = 0; i < cleanedRecords.length; i += batchSize) {
        batches.push(cleanedRecords.slice(i, i + batchSize));
      }

      setCopyProgress({
        phase: "uploading",
        message: `Uploading ${cleanedRecords.length} records in ${batches.length} batch(es)...`,
        recordsRead: records.length,
        batchesTotal: batches.length,
        batchesDone: 0,
      });

      const toApiBase = `https://${toServer}/api`;

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const isFirstBatch = i === 0;

        setCopyProgress({
          phase: "uploading",
          message: `Uploading batch ${i + 1}/${batches.length} (${batch.length} records)...`,
          recordsRead: records.length,
          batchesTotal: batches.length,
          batchesDone: i,
        });

        const uploadUrl = `${toApiBase}/v2-alpha/records.jsonl?sheetId=${encodeURIComponent(toSheetId)}&stream=true&truncate=${truncateDestination && isFirstBatch}&silent=false`;

        const jsonlData = `${batch.map(record => JSON.stringify(record)).join("\n")}\n`;

        const uploadResponse = await fetch(uploadUrl, {
          method: "POST",
          headers: {
            "Authorization": authHeader(toToken),
            "Content-Type": "application/jsonl",
          },
          body: jsonlData,
        });

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text().catch(() => "");
          throw new Error(`Upload failed on batch ${i + 1}: ${uploadResponse.status} ${uploadResponse.statusText}${errorText ? ` - ${errorText}` : ""}`);
        }
      }

      setCopyProgress({
        phase: "complete",
        message: `Successfully copied ${cleanedRecords.length} records`,
        recordsRead: records.length,
        recordsWritten: cleanedRecords.length,
        batchesTotal: batches.length,
        batchesDone: batches.length,
      });

      showSuccess(`Successfully copied ${cleanedRecords.length} records from source to destination.`);
    } catch (e: any) {
      setCopyProgress({
        phase: "error",
        message: e.message || "An error occurred during copy",
      });
      showError(e.message || "Copy failed", "Copy Error");
    }
  }

  const isCopying = copyProgress.phase === "streaming" || copyProgress.phase === "uploading";
  const canStartCopy = fromSheetId && fromToken && toSheetId && toToken && !isCopying;

  return (
    <div className="flex flex-col h-full">
      <DetailHeader breadcrumb={[{ label: "Tools" }]} />

      <div className="flex-1 overflow-auto p-6">
        <div className="flex gap-6 items-start">
          {/* Commit Completion Tool */}
          <div className="border border-border rounded-lg flex-1">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold">Commit Completion Tool</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Find and complete all incomplete commits for a workbook.
              </p>
            </div>

            <div className="p-4 space-y-4">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Server</label>
                <Combobox
                  value={commitServer}
                  onChange={(val) => setCommitServer((val as ServerHost) || "adp.flatfile.com")}
                  options={SERVER_OPTIONS}
                  placeholder="Select server..."
                  className="text-xs"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Access token</label>
                <input
                  className="w-full px-3 py-2.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Bearer ..."
                  type="password"
                  value={commitToken}
                  onChange={(e) => setCommitToken(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Workbook ID</label>
                <input
                  className="w-full px-3 py-2.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="adp_wb_..."
                  value={workbookId}
                  onChange={(e) => setWorkbookId(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={loadIncompleteCommits}
                  disabled={!workbookId || !commitToken || isLoadingCommits || isCompletingCommits}
                  size="sm"
                  variant="secondary"
                  className="text-xs h-9 cursor-pointer"
                >
                  {isLoadingCommits ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Find Incomplete Commits"
                  )}
                </Button>
                <Button
                  onClick={completeAllCommits}
                  disabled={commits.length === 0 || isLoadingCommits || isCompletingCommits}
                  size="sm"
                  className="text-xs h-9 cursor-pointer"
                >
                  {isCompletingCommits ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                      Completing...
                    </>
                  ) : (
                    `Complete All (${commits.length})`
                  )}
                </Button>
              </div>
            </div>

            {(commits.length > 0 || commitResults.length > 0) && (
              <div className="border-t border-border">
                <div className="p-4">
                  <h3 className="text-sm font-medium mb-3">
                    {commitResults.length > 0 ? "Results" : "Incomplete Commits"}
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {commits.map((commit) => {
                      const result = commitResults.find(r => r.commitId === commit.id);
                      return (
                        <div
                          key={commit.id}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-md"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-mono truncate">{commit.id}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {commit.createdAt && new Date(commit.createdAt).toLocaleString()}
                              {commit.sheetId && ` • Sheet: ${commit.sheetId}`}
                            </div>
                          </div>
                          <div className="ml-3 flex-shrink-0">
                            {result ? (
                              result.success ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                              ) : (
                                <div className="flex items-center gap-1">
                                  <XCircle className="w-4 h-4 text-destructive" />
                                  <span className="text-[10px] text-destructive max-w-32 truncate" title={result.error}>
                                    {result.error}
                                  </span>
                                </div>
                              )
                            ) : isCompletingCommits ? (
                              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                            ) : (
                              <span className="text-[10px] text-muted-foreground">Pending</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sheet Copy Tool */}
          <div className="border border-border rounded-lg flex-1">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold">Sheet Copy Tool</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Copy all records from one sheet to another (can copy across servers).
              </p>
            </div>

            <div className="p-4">
              <div className="flex gap-4">
                {/* FROM Section */}
                <div className="flex-1 space-y-3">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">From (Source)</div>
                  
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Server</label>
                    <Combobox
                      value={fromServer}
                      onChange={(val) => setFromServer((val as ServerHost) || "adp.flatfile.com")}
                      options={SERVER_OPTIONS}
                      placeholder="Select server..."
                      className="text-xs"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Access token</label>
                    <input
                      className="w-full px-3 py-2 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Bearer ..."
                      type="password"
                      value={fromToken}
                      onChange={(e) => setFromToken(e.target.value)}
                      autoComplete="off"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Sheet ID</label>
                    <input
                      className="w-full px-3 py-2 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="adp_sh_..."
                      value={fromSheetId}
                      onChange={(e) => setFromSheetId(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex items-center justify-center pt-6">
                  <ArrowRight className="w-5 h-5 text-muted-foreground" />
                </div>

                {/* TO Section */}
                <div className="flex-1 space-y-3">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">To (Destination)</div>
                  
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Server</label>
                    <Combobox
                      value={toServer}
                      onChange={(val) => setToServer((val as ServerHost) || "adp.flatfile.com")}
                      options={SERVER_OPTIONS}
                      placeholder="Select server..."
                      className="text-xs"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Access token</label>
                    <input
                      className="w-full px-3 py-2 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="Bearer ..."
                      type="password"
                      value={toToken}
                      onChange={(e) => setToToken(e.target.value)}
                      autoComplete="off"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Sheet ID</label>
                    <input
                      className="w-full px-3 py-2 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder="adp_sh_..."
                      value={toSheetId}
                      onChange={(e) => setToSheetId(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="truncateDestination"
                    checked={truncateDestination}
                    onChange={(e) => setTruncateDestination(e.target.checked)}
                    className="w-4 h-4 rounded border-border"
                    disabled={isCopying}
                  />
                  <label htmlFor="truncateDestination" className="text-xs text-muted-foreground cursor-pointer">
                    Clear destination sheet before copying (truncate)
                  </label>
                </div>
              </div>

              {/* Action Button */}
              <div className="mt-4">
                <Button
                  onClick={copySheet}
                  disabled={!canStartCopy}
                  size="sm"
                  className="text-xs h-9 cursor-pointer w-full"
                >
                  {isCopying ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                      Copying...
                    </>
                  ) : (
                    "Start Copy"
                  )}
                </Button>
              </div>
            </div>

            {/* Progress/Results Section */}
            {copyProgress.phase !== "idle" && (
              <div className="border-t border-border p-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {copyProgress.phase === "streaming" || copyProgress.phase === "uploading" ? (
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    ) : copyProgress.phase === "complete" ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : copyProgress.phase === "error" ? (
                      <XCircle className="w-4 h-4 text-destructive" />
                    ) : null}
                    <span className={`text-sm ${copyProgress.phase === "error" ? "text-destructive" : ""}`}>
                      {copyProgress.message}
                    </span>
                  </div>

                  {(copyProgress.batchesTotal && copyProgress.batchesDone !== undefined) && (
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${(copyProgress.batchesDone / copyProgress.batchesTotal) * 100}%` }}
                      />
                    </div>
                  )}

                  {copyProgress.phase === "complete" && copyProgress.recordsWritten !== undefined && (
                    <div className="text-xs text-muted-foreground">
                      {copyProgress.recordsWritten} records copied in {copyProgress.batchesTotal} batch(es)
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
